import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import type { JSX } from 'react';
import React, { useState, useCallback, useEffect } from 'react';

import InputMask from 'react-input-mask';
import { useNavigate } from 'react-router-dom';

import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import { analyticsService } from 'services/analytics/analyticsService';

import { validateCPF } from 'utils/validation';

import styles from './styles'; // Certifique-se de que este caminho está correto
import { IAddress } from 'utils/types/User';

const currentYear = new Date().getFullYear();

const SubscriptionPersonalForm: React.FC = () => {
  const { selectedPlan } = useSubscription();

  const [formData, setFormData] = useState<{
    fullName: string;
    cpf: string;
    phoneNumber: string;
    day: string;
    month: string;
    year: string;
    cep: string;
    address: IAddress;
    number: string;
    complement: string;
    reference: string;
    city: string;
    email: string;
    uf: string;
    neighborhood: string;
  }>({
    fullName: '',
    cpf: '',
    phoneNumber: '',
    day: '',
    month: '',
    year: '',
    cep: '',
    address: {
      street: '',
      neighborhood: '',
      city: '',
      zip: '',
      number: '',
      complement: '',
      reference: '',
      recipient: '',
      uf: '',
    },
    number: '',
    complement: '',
    reference: '',
    city: '',
    email: '',
    uf: '',
    neighborhood: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const { setPersonalData } = useSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    if (!selectedPlan) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate('/'); // Redirect if no data
    } else {
      // Track subscription personal form step
      analyticsService.trackSubscriptionFlow(
        'personal_form',
        selectedPlan.type,
      );
    }
  }, [navigate, selectedPlan]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { name, value } = e.target;
    if (name === 'cep') {
      setFormData(prev => ({
        ...prev,
        cep: value,
        city: '',
        uf: '',
        neighborhood: '',
        address: {
          street: '',
          neighborhood: '',
          city: '',
          zip: '',
          number: prev.number,
          complement: prev.complement,
          reference: prev.reference,
          recipient: '',
          uf: '',
        },
      }));
      setErrors(prev => ({ ...prev, cep: '', address: '', city: '', uf: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  const handleSelectChange = (event: SelectChangeEvent<string>): void => {
    const { name, value } = event.target;
    if (name) {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = useCallback(async () => {
    const newErrors: Record<string, string> = {};

    if (!validateCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }

    Object.keys(formData).forEach(key => {
      const value = formData[key as keyof typeof formData];
      if (
        key !== 'complement' &&
        key !== 'reference' &&
        key !== 'address' &&
        (!value || (typeof value === 'string' && value.trim() === ''))
      ) {
        newErrors[key] = 'Campo obrigatório';
      }
    });

    const cepDigits = formData.cep.replace(/\D/g, '');
    if (cepDigits.length !== 8) {
      newErrors.cep = 'Digite um CEP válido (8 dígitos).';
    } else if (
      !formData.address.city ||
      !formData.address.uf ||
      !formData.city ||
      !formData.uf
    ) {
      newErrors.cep =
        'CEP inválido ou não encontrado. Busque um CEP válido antes de continuar.';
      newErrors.address = 'Endereço obrigatório. Informe um CEP válido.';
      newErrors.city = 'Campo obrigatório';
      newErrors.uf = 'Campo obrigatório';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        setPersonalData(formData); // save data to context

        // Save data to localStorage for later use after login
        localStorage.setItem('pendingUserData', JSON.stringify(formData));

        // create token
        const response = await axios.post(
          'https://us-central1-clube-42-3baa5.cloudfunctions.net/generateAuthToken',
          {
            uid: formData.cpf, // use CPF as UID
            email: formData.email,
            additionalClaims: {
              fullName: formData.fullName,
              phone: formData.phoneNumber,
              cidade: formData.city,
            },
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'Access-Control-Allow-Origin': 'http://localhost:3000',
            },
          },
        );

        const { apiToken } = response.data;

        if (!apiToken) {
          throw new Error('Erro ao gerar token. Token inválido ou ausente.');
        }

        setToken(apiToken);

        // Save data to Firestore using the user's UID as document ID
        // Note: We don't have the user yet, so we'll need to handle this after login

        // navigate to next step
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        navigate('/login');
      } catch (error) {
        console.error('Erro ao salvar os dados no Firestore:', error);
      }
    } else {
      console.error('Erros no formulário:', newErrors);
    }
  }, [formData, navigate, setPersonalData, token]);

  const handleCepBlur = useCallback(async () => {
    const cepDigits = formData.cep.replace(/\D/g, '');

    if (!cepDigits) {
      setErrors(prev => ({ ...prev, cep: 'CEP é obrigatório.' }));
      return;
    }

    if (cepDigits.length !== 8) {
      setErrors(prev => ({
        ...prev,
        cep: 'Digite um CEP válido (8 dígitos).',
      }));
      setFormData(prev => ({
        ...prev,
        city: '',
        uf: '',
        neighborhood: '',
        address: {
          ...prev.address,
          street: '',
          neighborhood: '',
          city: '',
          zip: '',
          recipient: '',
          uf: '',
        },
      }));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `https://viacep.com.br/ws/${cepDigits}/json/`,
      );
      if (response.data.erro || !response.data.localidade) {
        setErrors(prev => ({ ...prev, cep: 'CEP não encontrado.' }));
        setFormData(prev => ({
          ...prev,
          city: '',
          uf: '',
          neighborhood: '',
          address: {
            ...prev.address,
            street: '',
            neighborhood: '',
            city: '',
            zip: '',
            recipient: '',
            uf: '',
          },
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          city: response.data.localidade,
          uf: response.data.uf || '',
          neighborhood: response.data.bairro || '',
          address: {
            street: response.data.logradouro || '',
            neighborhood: response.data.bairro || '',
            city: response.data.localidade,
            zip: cepDigits,
            number: prev.number,
            complement: prev.complement,
            reference: prev.reference,
            recipient: prev.fullName,
            uf: response.data.uf || '',
          },
        }));
        setErrors(prev => ({
          ...prev,
          cep: '',
          address: '',
          city: '',
          uf: '',
          neighborhood: '',
        }));
      }
    } catch {
      setErrors(prev => ({
        ...prev,
        cep: 'Erro ao buscar o CEP. Tente novamente.',
      }));
      setFormData(prev => ({
        ...prev,
        city: '',
        uf: '',
        neighborhood: '',
        address: {
          ...prev.address,
          street: '',
          neighborhood: '',
          city: '',
          zip: '',
          recipient: '',
          uf: '',
        },
      }));
    } finally {
      setLoading(false);
    }
  }, [formData.cep]);

  return (
    <Box sx={styles.container}>
      <Grid container spacing={4} sx={styles.gridContainer}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={styles.title}>
            Cadastro de Dados Pessoais
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Card sx={styles.card}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome Completo"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="CPF"
                    name="cpf"
                    value={formData.cpf}
                    onChange={handleInputChange}
                    error={!!errors.cpf}
                    helperText={errors.cpf}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="E-mail"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputMask
                    mask="(99) 99999-9999"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    maskChar={null}
                  >
                    {(
                      inputProps: React.InputHTMLAttributes<HTMLInputElement>,
                    ): JSX.Element => {
                      const { size: _size, ...rest } = inputProps;
                      return (
                        <TextField
                          {...rest}
                          fullWidth
                          label="Telefone"
                          name="phone"
                          error={!!errors.phone}
                          helperText={
                            errors.phone ||
                            'Digite o telefone no formato (99) 99999-9999'
                          }
                          variant="outlined"
                          color="primary"
                        />
                      );
                    }}
                  </InputMask>
                </Grid>
                <Grid item xs={12}>
                  <Typography>Data de Nascimento</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <FormControl fullWidth error={!!errors.day}>
                        <InputLabel>Dia</InputLabel>
                        <Select
                          name="day"
                          value={formData.day}
                          onChange={handleSelectChange}
                        >
                          {Array.from({ length: 31 }, (_, i) => (
                            <MenuItem key={i + 1} value={i + 1}>
                              {i + 1}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl fullWidth error={!!errors.month}>
                        <InputLabel>Mês</InputLabel>
                        <Select
                          name="month"
                          value={formData.month}
                          onChange={handleSelectChange}
                        >
                          {Array.from({ length: 12 }, (_, i) => (
                            <MenuItem key={i + 1} value={i + 1}>
                              {i + 1}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl fullWidth error={!!errors.year}>
                        <InputLabel>Ano</InputLabel>
                        <Select
                          name="year"
                          value={formData.year}
                          onChange={handleSelectChange}
                        >
                          {Array.from({ length: 100 }, (_, i) => (
                            <MenuItem
                              key={currentYear - i}
                              value={currentYear - i}
                            >
                              {currentYear - i}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={styles.subtitle}>
                      Endereço
                    </Typography>
                  </Grid>
                  <TextField
                    fullWidth
                    label="CEP"
                    name="cep"
                    value={formData.cep}
                    onChange={handleInputChange}
                    onBlur={handleCepBlur}
                    error={!!errors.cep}
                    helperText={errors.cep}
                    InputProps={{
                      endAdornment: loading && <CircularProgress size={20} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Endereço"
                    name="address"
                    value={
                      formData.address.street
                        ? `${formData.address.street}, ${formData.address.neighborhood}, ${formData.address.city}`
                        : formData.address.city
                          ? `${formData.address.city} - ${formData.address.uf}`
                          : ''
                    }
                    onChange={handleInputChange}
                    error={!!errors.address}
                    helperText={errors.address}
                    disabled
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Número"
                    name="number"
                    value={formData.number}
                    onChange={handleInputChange}
                    error={!!errors.number}
                    helperText={errors.number}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Complemento"
                    name="complement"
                    value={formData.complement}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Referência"
                    name="reference"
                    value={formData.reference}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" fullWidth onClick={handleSubmit}>
                    Avançar
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubscriptionPersonalForm;
